import * as React from 'react'
// libs
import { useLazyQuery, useMutation } from '@apollo/client/react'
// components
import { Header } from 'components/Modals/PlanItems/styles'
import { DrawerModal } from 'components/UI'
import SyncRulesForm, { SyncRulesFormData } from './components/Form'
// hooks
import { useApolloClient, useInitialValues, useModalData } from 'hooks/index'
// constants
import { Modals } from 'constants/index'
import { AUTH_METHOD_OPTIONS } from 'constants/users'
import { FILTER_ACTIONS, FILTER_FIELD_NAMES } from './components/Form/components/Filters'
import {
  TransactionName,
  ACTIONS_OPTIONS,
  JOB_TITLE_OPTIONS,
  PROPERTIES_OPTIONS,
  CONDITION_FIELD_OPTIONS
} from './components/Form/components/Rules/constants'
// icons
import { CloseIcon } from 'components/UI/Icons'
// types
import {
  ITokenMapping,
  IBusinessUnitsResponse,
  SystemProcessConditionAction,
  SystemProcessTransactionType
} from 'types/graphql'
// graphql
import { UPDATE_ACTIVE_DIRECTORY } from 'graphql/active-directory/mutations'
import { BUSINESS_UNITS_SELECT_QUERY } from 'graphql/businessUnit/queries'
// utils
import { v4 as uuid } from 'uuid'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'
import { getOptionByValue, getValueFromOption } from 'utils/form'

type QueryResponse = { businessUnits: IBusinessUnitsResponse }

const SyncRulesModal: React.FC = () => {
  const { client } = useApolloClient()

  const [getUnits] = useLazyQuery<QueryResponse>(BUSINESS_UNITS_SELECT_QUERY)
  const [updateToken] = useMutation(UPDATE_ACTIVE_DIRECTORY)

  const {
    hideModal,
    modalData: { isOpen, token }
  } = useModalData({ modalType: Modals.SYNC_RULES_MODAL })

  const getInitialValues = async () => {
    if (!token) {
      return {}
    }

    const unitsIds = token.mappings
      .filter((item: ITokenMapping) => item.transactions[0].fieldName === 'businessUnitId')
      .map((item: ITokenMapping) => item.transactions[0].fieldValue)

    let fieldValueOptions = AUTH_METHOD_OPTIONS

    if (unitsIds?.length) {
      const res = await getUnits({
        variables: { filter: { ids: unitsIds } }
      })

      fieldValueOptions = [
        ...AUTH_METHOD_OPTIONS,
        ...res.data.businessUnits.data.map((unit) => ({
          label: unit.description,
          value: unit.id
        }))
      ]
    }

    return {
      mappings: token.mappings.map((item: ITokenMapping) => ({
        ...item,
        conditions: item.conditions.map((condition) => ({
          ...condition,
          action: getOptionByValue(condition.action, ACTIONS_OPTIONS),
          fieldName: getOptionByValue(condition.fieldName, CONDITION_FIELD_OPTIONS)
        })),
        transactions: item.transactions.map((transaction) => ({
          ...transaction,
          fieldName: getOptionByValue(transaction.fieldName, [
            { label: 'Job Title', value: TransactionName.JobTitle },
            ...PROPERTIES_OPTIONS
          ]),
          fieldValue: getOptionByValue(transaction.fieldValue, [...fieldValueOptions, ...JOB_TITLE_OPTIONS])
        }))
      })),
      filterMappings: token.filterMappings?.map((item: ITokenMapping) => ({
        ...item,
        conditions: item.conditions.map((condition) => ({
          ...condition,
          action: getOptionByValue(condition.action, FILTER_ACTIONS),
          fieldName: getOptionByValue(condition.fieldName, FILTER_FIELD_NAMES)
        }))
      }))
    }
  }

  const onSubmit = async (data: SyncRulesFormData) => {
    try {
      const mappings = data.mappings.map((item) => ({
        id: item.id || uuid(),
        conditions: item.conditions.map((condition) => ({
          value: condition.value,
          action: getValueFromOption(condition.action),
          fieldName: getValueFromOption(condition.fieldName)
        })),
        transactions: item.transactions.map((transaction) => ({
          id: transaction.id || uuid(),
          fieldName: getValueFromOption(transaction.fieldName),
          fieldValue: getValueFromOption(transaction.fieldValue),
          transactionType:
            item.conditions[0].action.value === SystemProcessConditionAction.AlwaysTrue
              ? SystemProcessTransactionType.MapField
              : SystemProcessTransactionType.SetField
        }))
      }))

      const filterMappings = data.filterMappings.map((item) => ({
        id: item.id || uuid(),
        conditions: item.conditions.map((condition) => ({
          value: condition.value,
          action: getValueFromOption(condition.action),
          fieldName: getValueFromOption(condition.fieldName)
        })),
        transactions: []
      }))

      await updateToken({
        variables: {
          input: {
            id: token.id,
            mappings,
            filterMappings
          }
        }
      })

      client.cache.modify({
        id: `Token:${token.id}`,
        fields: {
          mappings() {
            return mappings
          },
          filterMappings() {
            return filterMappings
          }
        }
      })

      hideModal()
    } catch (e) {
      console.log('[SyncRulesModal.onSubmit]: ', parseGraphQLErrorMessages(e))
    }
  }

  const initialValues = useInitialValues(getInitialValues, token)

  return (
    <DrawerModal width='700px' isOpen={isOpen} hideModal={hideModal}>
      <Header>
        Sync Rules
        <CloseIcon onClick={hideModal} />
      </Header>
      <SyncRulesForm onSubmit={onSubmit} onCancel={hideModal} initialValues={initialValues} />
    </DrawerModal>
  )
}

export default SyncRulesModal
